import { z } from "zod";

/**
 * Tenant branding (openspec change `tenant-branding`): a display name, an accent per theme and a
 * logo per theme. The name and accents are settings registry keys; the logos have their own
 * records and are served same-origin by `GET /api/branding/logo/:variant/:logoId/:version`.
 */

export const LOGO_VARIANTS = ["light", "dark"] as const;
export const logoVariantSchema = z.enum(LOGO_VARIANTS);
export type LogoVariant = z.infer<typeof logoVariantSchema>;

/** PNG and WebP only: neither can carry script or external references (SVG is deferred). */
export const LOGO_MEDIA_TYPES = ["image/png", "image/webp"] as const;
export const logoMediaTypeSchema = z.enum(LOGO_MEDIA_TYPES);
export type LogoMediaType = z.infer<typeof logoMediaTypeSchema>;

/** Upload body cap (1 MiB), enforced before the body is read in full. */
export const LOGO_MAX_BYTES = 1024 * 1024;
/** Each side of a logo, in pixels, from the image header. */
export const LOGO_MIN_SIDE = 32;
export const LOGO_MAX_SIDE = 2048;
/** Width × height: bounds what a browser decodes. */
export const LOGO_MAX_PIXELS = 4_000_000;

/**
 * Why an upload was refused with `422 logo_invalid`: the declared type is not accepted, the
 * signature does not match it, a side or the pixel count is out of bounds, the image is
 * animated, or the header cannot be read.
 */
export const LOGO_INVALID_REASONS = [
  "type",
  "signature",
  "dimensions",
  "pixels",
  "animated",
  "malformed",
] as const;
export const logoInvalidReasonSchema = z.enum(LOGO_INVALID_REASONS);
export type LogoInvalidReason = z.infer<typeof logoInvalidReasonSchema>;

/**
 * Controls (tab and newline included), format and default-ignorable code points (zero-width,
 * bidi, BOM, variation selectors…) and Unicode tags: a name with any of them is refused, never
 * stripped.
 */
const HIDDEN = /[\p{Cc}\p{Cf}\p{Default_Ignorable_Code_Point}\u{E0000}-\u{E007F}]/u;

export const BRANDING_DISPLAY_NAME_MAX = 60;

/** Checked before trimming, which would otherwise drop a leading BOM silently. */
export const brandingDisplayNameSchema = z
  .string()
  .refine((value) => !HIDDEN.test(value), {
    message: "The name must not contain control or invisible characters",
  })
  .trim()
  .min(1)
  .max(BRANDING_DISPLAY_NAME_MAX);

/** `#rrggbb` only: the value ends up in a `<style>` element, so nothing else is accepted. */
export const brandingAccentSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Use a six-digit hex colour such as #3b5bdb");

/** The same-origin path of a logo: tenant from the session, never in the path. */
export function logoPath(variant: LogoVariant, logoId: string, version: number): string {
  return `/api/branding/logo/${variant}/${logoId}/${version}`;
}

const logoLocationSchema = z.object({ url: z.string() }).nullable();

/** A session's branding (`GET /api/me`): every element null when unset. */
export const brandingSchema = z.object({
  displayName: z.string().nullable(),
  accentLight: z.string().nullable(),
  accentDark: z.string().nullable(),
  logoLight: logoLocationSchema,
  logoDark: logoLocationSchema,
});
export type Branding = z.infer<typeof brandingSchema>;

/** A stored logo as tenant admins see it: metadata and its same-origin path, never the bytes. */
export const logoRecordSchema = z.object({
  variant: logoVariantSchema,
  url: z.string(),
  contentType: logoMediaTypeSchema,
  width: z.int().positive(),
  height: z.int().positive(),
  sizeBytes: z.int().positive(),
  sha256: z.string(),
  createdAt: z.string(),
});
export type LogoRecord = z.infer<typeof logoRecordSchema>;

/** The Branding screen's record (`GET /api/admin/branding`). */
export const adminBrandingSchema = z.object({
  displayName: z.string().nullable(),
  accentLight: z.string().nullable(),
  accentDark: z.string().nullable(),
  logoLight: logoRecordSchema.nullable(),
  logoDark: logoRecordSchema.nullable(),
});
export type AdminBranding = z.infer<typeof adminBrandingSchema>;
